'use client';

import type { Mutate, StoreApi, UseBoundStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { createTrackedStore } from '@/lib/dev/zustand-registry';
import type { Network } from '@/lib/network/types';
import { logger } from '@/lib/telemetry/logger';

export const activeTradeAttempts: Set<string> = new Set<string>();

type AttemptStatus = 'pending' | 'unknown' | 'success' | 'error' | 'cancelled';
export interface JournalAttempt {
  userId: string;
  network: Network;
  startedAt: number;
  recoveryStoppedAt?: number;
  status: AttemptStatus;
  signature?: string;
  submissionTraces?: string[];
  finalSubmissionTrace?: string;
  // ALLOW-UNKNOWN: serialized analytics metadata is a vendor boundary.
  properties: Record<string, unknown>;
}
interface JournalState {
  attempts: Record<string, JournalAttempt>;
  pruneExpired: () => void;
}

export const useTradeAttemptJournal: UseBoundStore<
  Mutate<StoreApi<JournalState>, [['zustand/persist', JournalState]]>
> = createTrackedStore<JournalState>('useTradeAttemptJournal')(
  persist(
    (set) => ({
      attempts: {},
      pruneExpired: () => set((state) => ({ attempts: pruneExpiredAttempts(state.attempts) })),
    }),
    {
      name: 'morfi-trade-attempt-journal',
      onRehydrateStorage: () => (state: JournalState | undefined) => state?.pruneExpired(),
    },
  ),
);

/** Persist before dispatch, and suppress repeated terminal callbacks after recovery. */
export function recordAttemptEvent(
  name: string,
  // ALLOW-UNKNOWN: SDK analytics emitter boundary.
  properties: Record<string, unknown>,
  userId: string | null,
): boolean {
  if (name === 'trade_submission') {
    try {
      recordSubmission(properties);
    } catch {
      logger.warn('analytics.trade_journal_write_failed', { eventName: name });
    }
    return false;
  }
  if (
    ![
      'trade_attempted',
      'trade_success',
      'trade_error',
      'trade_cancelled',
      'trade_unknown',
    ].includes(name)
  )
    return true;
  if (!['buy', 'sell', 'swap'].includes(String(properties.instruction))) return true;
  const { traceId, network } = properties;
  if (
    typeof traceId !== 'string' ||
    userId === null ||
    userId === '' ||
    (network !== 'devnet' && network !== 'mainnet' && network !== 'local')
  )
    return true;
  if (name === 'trade_attempted') activeTradeAttempts.add(traceId);
  else activeTradeAttempts.delete(traceId);
  const previous = useTradeAttemptJournal.getState().attempts[traceId];
  if (
    previous !== undefined &&
    (previous.userId !== userId ||
      ['success', 'error'].includes(previous.status) ||
      name === 'trade_attempted' ||
      (name === 'trade_cancelled' && previous.status === 'cancelled'))
  )
    return false;
  const status: AttemptStatus =
    name === 'trade_attempted'
      ? 'pending'
      : name === 'trade_success'
        ? 'success'
        : name === 'trade_error'
          ? 'error'
          : name === 'trade_cancelled'
            ? 'cancelled'
            : 'unknown';
  const record: JournalAttempt = {
    ...previous,
    userId,
    network,
    startedAt: previous?.startedAt ?? Date.now(),
    status,
    properties: { ...previous?.properties, ...properties },
    ...(previous?.signature === undefined ? {} : { signature: previous.signature }),
    ...(typeof properties.signature === 'string' && properties.signature.length > 0
      ? { signature: properties.signature }
      : {}),
  };
  try {
    useTradeAttemptJournal.setState((state) => ({
      attempts: { ...pruneExpiredAttempts(state.attempts), [traceId]: record },
    }));
  } catch {
    logger.warn('analytics.trade_journal_write_failed', { traceId, eventName: name });
  }
  return true;
}

export function recordAttemptSignature(traceId: string, signature: string): void {
  if (signature.length === 0) return;
  const entry = Object.entries(useTradeAttemptJournal.getState().attempts).find(
    ([id, attempt]) => id === traceId || attempt.finalSubmissionTrace === traceId,
  );
  if (entry === undefined) return;
  const [attemptId, previous] = entry;
  // A successful sell leg alone cannot complete a sequential swap.
  if (previous.finalSubmissionTrace !== undefined && previous.finalSubmissionTrace !== traceId)
    return;
  if (previous === undefined || previous.signature === signature) return;
  try {
    useTradeAttemptJournal.setState((state) => ({
      attempts: { ...state.attempts, [attemptId]: { ...previous, signature } },
    }));
  } catch {
    logger.warn('analytics.trade_journal_write_failed', {
      traceId,
      eventName: 'signature_observed',
    });
  }
}

// ALLOW-UNKNOWN: SDK transaction metadata boundary.
function recordSubmission(properties: Record<string, unknown>): void {
  const { traceId, submissionTraceId, isFinalLeg } = properties;
  if (typeof traceId !== 'string' || typeof submissionTraceId !== 'string') return;
  const attempt = useTradeAttemptJournal.getState().attempts[traceId];
  if (attempt === undefined) return;
  useTradeAttemptJournal.setState((state) => ({
    attempts: {
      ...state.attempts,
      [traceId]: {
        ...attempt,
        submissionTraces: [...(attempt.submissionTraces ?? []), submissionTraceId],
        ...(isFinalLeg === true ? { finalSubmissionTrace: submissionTraceId } : {}),
      },
    },
  }));
}

function pruneExpiredAttempts(
  attempts: Record<string, JournalAttempt>,
): Record<string, JournalAttempt> {
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return Object.fromEntries(
    Object.entries(attempts).filter(([, attempt]) => attempt.startedAt > cutoff),
  );
}
